import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import analyticsService from '../../services/analyticsService';
import api from '../../config/api';
import { formatUSD } from '../../utils/currencyFormatter';
import { Coffee } from 'lucide-react';
import toast from 'react-hot-toast';
import logger from '../../utils/logger';
import { emitSubscriptionRequired } from '../../utils/subscriptionEvents';
import { useShop } from '../../context/ShopContext';

const AnalyticsTab = () => {
  const { t } = useTranslation();
  const { shop } = useShop();
  const { subscriptionBlocked, setSubscriptionBlocked } = useOutletContext();
  const [analyticsData, setAnalyticsData] = useState({ todayKPIs: null, topProducts: null, weeklySales: null, averageOrderValue: null });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const getTodayDate = () => new Date().toISOString().split('T')[0];
  const getWeekAgoDate = () => { const d = new Date(); d.setDate(d.getDate()-7); return d.toISOString().split('T')[0]; };

  const loadAnalyticsData = useCallback(async () => {
    if (!shop?.id) return;
    if (subscriptionBlocked) return;
    setAnalyticsLoading(true);
    try {
      const [todayKPIsResponse, topProductsResponse, weeklySalesResponse, aovResponse] = await Promise.all([
        analyticsService.getTodayKPIs(shop.id),
        analyticsService.getTopProducts(shop.id, 7, 5),
        analyticsService.getWeeklySales(shop.id, getWeekAgoDate(), getTodayDate()),
        analyticsService.getAverageOrderValue(shop.id, 7)
      ]);
      setAnalyticsData({
        todayKPIs: todayKPIsResponse.success ? todayKPIsResponse.data : null,
        topProducts: topProductsResponse.success ? topProductsResponse.data : null,
        weeklySales: weeklySalesResponse.success ? weeklySalesResponse.data : null,
        averageOrderValue: aovResponse.success ? aovResponse.data : null
      });
    } catch (e) {
      toast.error(t('dashboard.failedAnalyticsLoad','Failed to load analytics data'));
    } finally { setAnalyticsLoading(false); }
  }, [shop?.id, subscriptionBlocked, t]);

  // Upfront gating similar to legacy logic
  useEffect(() => {
    (async () => {
      try {
        const response = await api.get('/auth/subscription-status');
        const data = response.data;
        const inactive = !data.hasActiveSubscription && data.status !== 'ADMIN' && data.status !== 'NOT_APPLICABLE';
        if (inactive) {
          emitSubscriptionRequired({ subscriptionStatus: data.status, expiryDate: data.expiryDate });
          setSubscriptionBlocked(true);
        } else {
          setSubscriptionBlocked(false);
          loadAnalyticsData();
        }
      } catch (e) {
        logger.error('Analytics gating status check failed:', e);
      }
    })();
  }, [loadAnalyticsData, setSubscriptionBlocked]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6"><h2 className="text-2xl font-bold text-white">{t('dashboard.analyticsContent.title')}</h2></div>
      {subscriptionBlocked ? (
        <div className="p-6 bg-red-600/20 border border-red-500/30 rounded-lg text-red-200">
          <h3 className="text-lg font-semibold mb-2">{t('subscription.analyticsBlockedTitle','Upgrade Required for Analytics')}</h3>
          <p className="text-sm mb-4">{t('subscription.analyticsBlockedMessage','Your subscription is inactive. Renew to access analytics insights again.')}</p>
          <button onClick={() => window.dispatchEvent(new CustomEvent('dashboard-switch-tab', { detail: { tab: 'billing' } }))} className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded">{t('renewNow','Renew Now')}</button>
        </div>
      ) : analyticsLoading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4" /><p className="text-white text-lg">{t('dashboard.loadingAnalytics','Loading analytics...')}</p></div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-xl font-bold text-white mb-4">{t('dashboard.analyticsContent.todayKPIs')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <KPIBlock value={analyticsData.todayKPIs?.revenue ? formatUSD(analyticsData.todayKPIs.revenue) : '$0'} label={t('dashboard.analyticsContent.todayRevenue')} color="text-[#a15507]" />
                <KPIBlock value={analyticsData.todayKPIs?.orders || 0} label={t('dashboard.analyticsContent.todayOrders')} color="text-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <KPIBlock value={analyticsData.todayKPIs?.qrOrders || 0} label={t('dashboard.analyticsContent.qrOrders')} color="text-green-500" large />
                <KPIBlock value={analyticsData.todayKPIs?.manualOrders || 0} label={t('dashboard.analyticsContent.manualOrders')} color="text-purple-500" large />
              </div>
            </div>
            <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-xl font-bold text-white mb-4">{t('dashboard.analyticsContent.orderStatistics')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <KPIBlock value={analyticsData.todayKPIs?.pending || 0} label={t('dashboard.analyticsContent.pendingOrders')} color="text-blue-500" />
                <KPIBlock value={analyticsData.averageOrderValue?.aov ? formatUSD(analyticsData.averageOrderValue.aov) : '$0'} label={t('dashboard.analyticsContent.avgOrderValue')} color="text-purple-500" />
              </div>
              {analyticsData.todayKPIs?.topItem && (
                <div className="mt-4 p-3 bg-[#374151] rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">{t('dashboard.analyticsContent.topItemToday')}</p>
                  <p className="text-white font-medium">{analyticsData.todayKPIs.topItem.name}</p>
                </div>
              )}
            </div>
          </div>
          <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
            <h3 className="text-xl font-bold text-white mb-4">{t('dashboard.analyticsContent.topPerformingProducts')}</h3>
            {analyticsData.topProducts?.products?.length > 0 ? (
              <div className="space-y-3">
                {analyticsData.topProducts.products.map((p, idx) => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-[#374151] rounded-lg">
                    <div className="flex items-center space-x-3"><span className="text-lg font-bold text-[#a15507]">#{idx+1}</span><span className="text-white font-medium">{p.name}</span></div>
                    <div className="text-right"><p className="text-white font-semibold">{p.count} {t('dashboard.analyticsContent.orders')}</p><p className="text-sm text-gray-400">{t('dashboard.analyticsContent.last7Days')}</p></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8"><Coffee className="w-10 h-10 text-gray-500 mx-auto mb-2" /><p className="text-gray-400">{t('dashboard.analyticsContent.noData','No analytics data available')}</p></div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const KPIBlock = ({ value, label, color, large }) => (
  <div className="text-center">
    <p className={`font-bold ${large? 'text-2xl':'text-3xl'} ${color}`}>{value}</p>
    <p className="text-sm text-gray-400">{label}</p>
  </div>
);

export default AnalyticsTab;
